import React, { useContext, useEffect, useState } from 'react';
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import AppContext from 'context/Context';
import classNames from 'classnames';
import NavbarTop from 'components/navbar/top/NavbarTop';
import NavbarVertical from 'components/navbar/vertical/NavbarVertical';
import useAPI from 'hooks/useApi';
import { getLocations } from 'api/endpoints/TerrestrialServices';
import { getMonitoringLocations } from 'api/endpoints/TerrestrialServices';
import { isEmpty, get, forIn } from 'lodash';
import { Col } from 'react-bootstrap';
import DropDownMenu from 'components/navbar/top/DropDownMenu';
import Loader from 'components/common/Loader';
import ProductProvider from 'components/app/e-commerce/ProductProvider';
import CourseProvider from 'components/app/e-learning/CourseProvider';
import { getItemFromStore } from 'helpers/utils';
import { jwtDecode } from 'jwt-decode';
import { getBlockList } from 'api/endpoints/ManagementServices';
import { getTenantFromToken } from 'utils/routeUtils';
import { useAuth } from 'hooks/useAuth';
import { getEnvProjList } from 'api/endpoints/Enviormental_Indicators';

const MainLayout = () => {
  const { hash, pathname } = useLocation();
  const isKanban = pathname.includes('kanban');
  const isEvaluate = pathname.includes('evaluate');
  const isMonitoring = pathname.includes('monitor');
  const isPlotOverlay = pathname.includes('plotoverlay');
  const isHabitat = pathname.includes('habitat');
  const { cluster } = useParams();
  const { isOffline } = useAuth();

  const navigate = useNavigate();

  const {
    config: { isFluid, navbarPosition }
  } = useContext(AppContext);

  // Get tenant ID
  const tenant = getTenantFromToken();

  const { setConfig } = useContext(AppContext);
  const [locationData, setLocationData] = useState([]);
  const [monitoringLocationData, setMonitoringLocationData] = useState([]);
  const [blockLocationData, setBlockLocationData] = useState([]);
  const [habitatLocationData, setHabitatLocationData] = useState([]);
  const [locationLoading, setLocationLoading] = useState(false);
  const [apisLoading, setApisLoading] = useState(cluster ? true : false);
  const [showDropdown, setShowDropdown] = useState(true);
  const [firstEvaluationCluster, setFirstEvaluationCluster] = useState('');
  const [firstMonitoringCluster, setFirstMonitoringCluster] = useState('');
  const [firstHabitatCluster, setFirstHabitatCluster] = useState('');
  const [firstBlockCluster, setFirstBlockCluster] = useState('');

  useEffect(() => {
    setTimeout(() => {
      if (hash) {
        const id = hash.replace('#', '');
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView({ block: 'start', behavior: 'smooth' });
        }
      }
    }, 0);
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);

    if (pathname === '/') {
      navigate(`/${tenant}/`);
    }

    const substrings = ['evaluate', 'monitor', 'plotoverlay', 'habitat'];
    setShowDropdown(substrings.some(substring => pathname.includes(substring)));
  }, [pathname]);

  // Only fetch API data if we're online - FOR NAVBAR DATA ONLY
  const getEvaluationLocationAPI = useAPI(getLocations);
  const getMonitoringLocationAPI = useAPI(getMonitoringLocations);
  const getBlockListAPI = useAPI(getBlockList);
  const getHabitatMonitoringAPI = useAPI(getEnvProjList);

  const apiHooks = [
    getEvaluationLocationAPI,
    getMonitoringLocationAPI,
    getHabitatMonitoringAPI
  ];

  // Clear dropdown data when route changes
  useEffect(() => {
    // Clear location data when switching routes
    setLocationData([]);
  }, [isEvaluate, isMonitoring, isPlotOverlay, isHabitat]);

  // Call appropriate API based on current route
  useEffect(() => {
    if (isOffline) return;

    if (isPlotOverlay) {
      getBlockListAPI.request(tenant);
    }

    if (isHabitat) {
      getHabitatMonitoringAPI.request(tenant);
    }
  }, [isPlotOverlay, isOffline, tenant, isHabitat]);

  const performSequentialAPICalls = async () => {
    if (isOffline) return; // Don't make API calls if offline

    setLocationLoading(true);
    for (let i = 0; i < apiHooks.length; i++) {
      await apiHooks[i].request(tenant);
    }

    // Also call block API if we're on plotoverlay
    if (isPlotOverlay) {
      await getBlockListAPI.request(tenant);
    }

    if (isHabitat) {
      await getHabitatMonitoringAPI.request(tenant);
    }
    setLocationLoading(false);
  };

  useEffect(() => {
    performSequentialAPICalls();
  }, [isOffline]);

  useEffect(() => {
    if (!isOffline) {
      // Process evaluation data for navbar
      if (!isEmpty(getEvaluationLocationAPI.data)) {
        const responseData = get(getEvaluationLocationAPI.data, 'results', {});
        const filterOptions = [];
        forIn(responseData, (value, key) => {
          filterOptions.push({ key, value });
        });
        if (!isEmpty(filterOptions)) {
          setFirstEvaluationCluster(filterOptions[0]?.key);
          // Only set locationData for evaluation
          if (isEvaluate) {
            setLocationData(filterOptions);
          }
        }
      }

      // Process monitoring data for navbar
      if (!isEmpty(getMonitoringLocationAPI.data)) {
        const responseData = get(getMonitoringLocationAPI.data, 'results', {});
        const filterOptions = [];
        forIn(responseData, (value, key) => {
          filterOptions.push({ key, value });
        });
        if (!isEmpty(filterOptions)) {
          setFirstMonitoringCluster(filterOptions[0]?.key);
          setMonitoringLocationData(filterOptions);
          // Only set locationData for monitoring
          if (isMonitoring) {
            setLocationData(filterOptions);
          }
        }
      }

      // Process block data for navbar
      if (!isEmpty(getBlockListAPI.data)) {
        const responseData = get(getBlockListAPI.data, 'results', {});
        const filterOptions = [];
        forIn(responseData, block => {
          filterOptions.push({ key: block.id, value: block.name });
        });
        if (!isEmpty(filterOptions)) {
          setFirstBlockCluster(filterOptions[0]?.key);
          setBlockLocationData(filterOptions);
          // Only set locationData for plotoverlay
          if (isPlotOverlay) {
            setLocationData(filterOptions);
          }
        }
      }

      // Process habitat data for navbar
      if (!isEmpty(getHabitatMonitoringAPI.data)) {
        const responseData = get(getHabitatMonitoringAPI.data, 'results', {});
        const filterOptions = [];
        forIn(responseData, block => {
          filterOptions.push({ key: block.id, value: block.name });
        });
        if (!isEmpty(filterOptions)) {
          setFirstHabitatCluster(filterOptions[0]?.key);
          setHabitatLocationData(filterOptions);
          // Only set locationData for habitat
          if (isHabitat) {
            setLocationData(filterOptions);
          }
        }
      }
    }
  }, [
    getEvaluationLocationAPI.data,
    getMonitoringLocationAPI.data,
    getBlockListAPI.data,
    getHabitatMonitoringAPI.data,
    isEvaluate,
    isMonitoring,
    isPlotOverlay,
    isOffline,
    isHabitat
  ]);

  // show loading in evaluation and monitoring pages without cluster
  if (!isOffline && (isEvaluate || isMonitoring || isHabitat)) {
    if (isEmpty(cluster)) {
      if (locationLoading) {
        return <Loader />;
      }
    }
  }

  // Determine if the current path is an offline route
  const isOfflineRoute =
    pathname.startsWith('/offline') ||
    pathname.startsWith('/farmer/') ||
    pathname.startsWith('/cultivation/create') ||
    pathname.startsWith('/offline-data') ||
    pathname.startsWith('/pending-sync');

  return (
    <div className={isFluid ? 'container-fluid' : 'container'}>
      {(navbarPosition === 'vertical' || navbarPosition === 'combo') &&
        tenant !== '29a333e402b4e010610d5783c274c0ed' && (
          <NavbarVertical
            monitoringLocationData={monitoringLocationData}
            blockLocationData={blockLocationData}
            habitatLocationData={habitatLocationData}
            firstEvaluationCluster={firstEvaluationCluster}
            firstMonitoringCluster={firstMonitoringCluster}
            firstBlockCluster={firstBlockCluster}
            firstHabitatCluster={firstHabitatCluster}
            isOffline={isOffline}
            isOfflineRoute={isOfflineRoute}
          />
        )}
      <ProductProvider>
        <CourseProvider>
          <div className={classNames('content', { 'pb-0': isKanban })}>
            <NavbarTop
              dropdownDisabled={isOffline || isOfflineRoute}
              locationData={locationData}
              showDropdown={showDropdown && !isOffline && !isOfflineRoute}
              locationLoading={locationLoading}
              isOffline={isOffline}
            />

            {/*------ Main Routes ------*/}
            <Outlet context={{ setApisLoading }} />
          </div>
        </CourseProvider>
      </ProductProvider>
    </div>
  );
};

export default MainLayout;
